import React, { useState } from 'react';
import { useCreatePath, useDelete, useNotify, useRecordContext, useSaveContext } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { useFormState, useWatch, useFormContext } from 'react-hook-form';
import { Box } from '@mui/material';
import { UserRole } from 'src/auth/types';
import { shsButtonStyles } from 'src/styles';
import { ShsButton, WithRole, ShsDeactivateConfirmDialog, ShsSuccessNotification } from 'src/components/common';
import { NOTIFICATION_OPTIONS_SUCCESS, Resource, DEACTIVATION_LABELS } from 'src/constants';
import { CancelEditModal, DeleteModal } from 'src/components/Children/modals';
import { ReactComponent as DeleteIcon } from 'src/assets/trash-can.svg';
import ShsFormToolbar from '../../common/ShsFormToolbar';
import { ValidationErrorModal } from '../modals/ValidationErrorModal';

/**
 * Props for the AddToolbar component
 * @property { boolean } [editMode] - Indicates if the toolbar is in edit mode
 * @property { () => void } [handleCancel] - Function to call when canceling an action
 */
interface AddToolbarProps {
    editMode?: boolean;
    handleCancel?: () => void;
    isChildRecordActive?: boolean;
}

/**
 * A toolbar component for adding or editing entries
 * It provides buttons for saving, canceling, and deleting entries
 */
const AddToolbar: React.FC<AddToolbarProps> = ({ handleCancel, editMode = false, isChildRecordActive = true }) => {
    const { isDirty, defaultValues } = useFormState();
    const { control, getValues, setValue } = useFormContext();

    const record = useRecordContext();
    const createPath = useCreatePath();
    const notify = useNotify();
    const [deleteOne] = useDelete();
    const navigate = useNavigate();
    const [isValidationErrorModalOpen, setValidationErrorModalOpen] = useState(false);
    const [isCancelModalOpen, setCancelModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [isDeactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const { save } = useSaveContext();

    const servicesEndDate = useWatch({ control, name: 'visit.servicesEndDate' });
    const isDeactivation = editMode && !!servicesEndDate && !defaultValues?.visit?.servicesEndDate;

    const childFullName = record
        ? `${record.lastName || ''} ${record.firstName || ''} ${record.patronymic || ''}`.trim()
        : '';

    /**
     * Opens the cancel confirmation modal
     */
    const handleCancelConfirm = () => {
        setCancelModalOpen(true);
    };

    /**
     * Closes the cancel confirmation modal
     */
    const handleCloseCancelModal = () => {
        setCancelModalOpen(false);
    };

    /**
     * Closes the delete confirmation modal
     */
    const handleOpenDeleteModal = () => {
        setDeleteModalOpen(true);
    };

    /**
     * Handles form validation error
     * Opens validation error modal
     */
    const handleValidationError = () => {
        handleCloseCancelModal();
        setValidationErrorModalOpen(true);
    };

    /**
     * Closes the validation error modal
     */
    const handleCloseValidationErrorModal = () => {
        setValidationErrorModalOpen(false);
    };

    /**
     * Handles the save button click
     */
    const handleSaveClick = (e?: any) => {
        if (isDeactivation) {
            if (e && e.preventDefault) e.preventDefault();
            setDeactivateModalOpen(true);
        }
    };

    /**
     * Closes the deactivate confirmation modal and resets servicesEndDate field
     */
    const handleCloseDeactivateModal = () => {
        setValue('visit.servicesEndDate', defaultValues?.visit?.servicesEndDate || null);
        setDeactivateModalOpen(false);
    };

    /**
     * Handles confirmation of deactivation
     */
    const handleDeactivateConfirm = () => {
        if (save) {
            save(getValues());
        }
        setDeactivateModalOpen(false);
    };

    /**
     * Handles the click event for the delete button
     */
    const handleDeleteBtnClick = () => {
        deleteOne(
            Resource.CHILDREN,
            { id: record.id },
            {
                mutationMode: 'pessimistic',
                onSuccess: () => {
                    notify(
                        <ShsSuccessNotification message="Видалення дитини пройшло успішно" />,
                        NOTIFICATION_OPTIONS_SUCCESS
                    );
                    navigate(createPath({ resource: 'children', type: 'list' }));
                },
                onError: (error: any) => {
                    notify(`Помилка: ${error?.message}`, { type: 'error' });
                }
            }
        );
        setDeleteModalOpen(false);
    };

    /**
     * Closes the delete confirmation modal
     */
    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
    };

    return (
        <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <ShsFormToolbar
                    submitLabel={isDeactivation ? 'Деактивувати' : undefined}
                    enableSaveButton={isDirty}
                    handleCancel={handleCancelConfirm}
                    sx={shsButtonStyles}
                    shouldCheckIfDirty={false}
                    handleSave={handleSaveClick}
                />

                {editMode && (
                    <WithRole roles={[UserRole.SYSTEM_ADMIN]}>
                        <ShsButton
                            label={'Bидалити'}
                            sx={{
                                marginTop: '28px'
                            }}
                            styleType="outline-danger"
                            icon={<DeleteIcon style={{ fill: '#F23030' }} />}
                            onClick={handleOpenDeleteModal}
                        />
                    </WithRole>
                )}
            </Box>

            <DeleteModal
                isOpen={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                onCancel={handleCancel}
                onConfirm={handleDeleteBtnClick}
            />

            <CancelEditModal
                isOpen={isCancelModalOpen}
                isEdit={editMode}
                onClose={handleCloseCancelModal}
                handleValidationError={handleValidationError}
                onCancel={handleCancel}
                isChildRecordActive={isChildRecordActive}
            />

            <ShsDeactivateConfirmDialog
                open={isDeactivateModalOpen}
                onClose={handleCloseDeactivateModal}
                onConfirm={handleDeactivateConfirm}
                title={DEACTIVATION_LABELS.CHILD.TITLE}
                confirmLabel={DEACTIVATION_LABELS.CHILD.CONFIRM}
                cancelLabel={DEACTIVATION_LABELS.CHILD.CANCEL}>
                Чи впевнені ви що хочете деактивувати {childFullName}?
                <br />
                <br />
                {DEACTIVATION_LABELS.CHILD.BODY_SUFFIX}
            </ShsDeactivateConfirmDialog>

            {isValidationErrorModalOpen && (
                <ValidationErrorModal isOpen={isValidationErrorModalOpen} onClose={handleCloseValidationErrorModal} />
            )}
        </>
    );
};

export default AddToolbar;
